import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import sessionManagementService from '../services/sessionManagementService';
import auditLogService from '../services/auditLogService';
import securityAlertService from '../services/securityAlertService';
import patternDetectionService from '../services/patternDetectionService';
import twoFactorAuthService from '../services/twoFactorAuthService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [sessionToken, setSessionToken] = useState(null)
  const [securityPreferences, setSecurityPreferences] = useState(null)
  const [currentOrganizationId, setCurrentOrganizationId] = useState(null)
  const [isSessionVerified, setIsSessionVerified] = useState(false)

  // Session timeout and token refresh intervals
  const inactivityTimerRef = useRef(null)
  const tokenRefreshIntervalRef = useRef(null)
  const lastActivityRef = useRef(Date.now())

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) {
          setUserProfile(data)
          // Load security preferences
          const { data: secPrefs } = await sessionManagementService?.getSecurityPreferences(userId)
          setSecurityPreferences(secPrefs)
          
          // Load user's default organization
          const { data: memberships } = await supabase?.from('team_members')?.select('organization_id')?.eq('user_id', userId)?.limit(1)
          if (memberships && memberships?.length > 0) {
            // Set first organization as default
            setCurrentOrganizationId(memberships?.[0]?.organization_id)
          }
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      setSecurityPreferences(null)
      setCurrentOrganizationId(null)
    }
  }

  // Session management operations
  const sessionOperations = {
    async create(session) {
      if (!session?.access_token) return
      setSessionToken(session?.access_token)
      
      // Create session entry in database
      await sessionManagementService?.createSession(session?.access_token)
      
      // Check for suspicious login patterns
      try {
        const deviceInfo = navigator?.userAgent || 'Unknown';
        const ipAddress = null; // Would need external service for IP detection
        const location = null; // Would need geolocation service
        
        await securityAlertService?.checkSuspiciousLogin(ipAddress, deviceInfo, location);
      } catch (error) {
        console.error('Error checking suspicious login:', error);
      }
      
      // Start inactivity timer and token refresh
      sessionOperations?.startInactivityTimer()
      sessionOperations?.startTokenRefresh()
    },

    startInactivityTimer() {
      if (!securityPreferences?.sessionTimeout?.enabled) return
      
      const timeoutDuration = (securityPreferences?.sessionTimeout?.duration || 30) * 60 * 1000 // Convert to ms
      
      // Clear existing timer
      if (inactivityTimerRef?.current) {
        clearTimeout(inactivityTimerRef?.current)
      }
      
      // Set new timer
      inactivityTimerRef.current = setTimeout(() => {
        if (securityPreferences?.sessionTimeout?.autoLogout) {
          console.log('Session timeout - auto logout')
          signOut()
        }
      }, timeoutDuration)
    },

    resetInactivityTimer() {
      lastActivityRef.current = Date.now()
      
      // Update session activity in database
      if (sessionToken) {
        sessionManagementService?.updateActivity(sessionToken)
      }
      
      // Restart timer
      sessionOperations?.startInactivityTimer()
    },

    startTokenRefresh() {
      if (!securityPreferences?.tokenRefresh?.enabled) return
      
      const refreshInterval = (securityPreferences?.tokenRefresh?.interval || 15) * 60 * 1000 // Convert to ms
      
      // Clear existing interval
      if (tokenRefreshIntervalRef?.current) {
        clearInterval(tokenRefreshIntervalRef?.current)
      }
      
      // Set new interval
      tokenRefreshIntervalRef.current = setInterval(async () => {
        try {
          const { data, error } = await supabase?.auth?.refreshSession()
          if (error) {
            console.error('Token refresh failed:', error)
            signOut()
          } else if (data?.session) {
            setSessionToken(data?.session?.access_token)
          }
        } catch (error) {
          console.error('Token refresh error:', error)
        }
      }, refreshInterval)
    },

    cleanup() {
      if (inactivityTimerRef?.current) {
        clearTimeout(inactivityTimerRef?.current)
      }
      if (tokenRefreshIntervalRef?.current) {
        clearInterval(tokenRefreshIntervalRef?.current)
      }
      setSessionToken(null)
    }
  }

  // Track user activity
  useEffect(() => {
    if (!user || !securityPreferences?.sessionTimeout?.enabled) return

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']
    
    const handleActivity = () => {
      const now = Date.now()
      const timeSinceLastActivity = now - lastActivityRef?.current
      
      // Only reset if more than 1 minute has passed
      if (timeSinceLastActivity > 60000) {
        sessionOperations?.resetInactivityTimer()
      }
    }

    activityEvents?.forEach(event => {
      window?.addEventListener(event, handleActivity)
    })

    return () => {
      activityEvents?.forEach(event => {
        window?.removeEventListener(event, handleActivity)
      })
    }
  }, [user, securityPreferences])

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
  onChange: (event, session) => {
    if (session?.user) {
      setUser(session?.user)
      setIsSessionVerified(true)   // ✅ ADD THIS
      setLoading(false)

      profileOperations?.load(session?.user?.id)
      sessionOperations?.create(session)
    } else {
      setUser(null)
      setIsSessionVerified(false)  // ✅ ADD THIS
      setLoading(false)

      profileOperations?.clear()
      sessionOperations?.cleanup()
    }
  }
}

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        
        if (session?.user) {
          // Check if this is an SSO login requiring 2FA
          const sso2FAContext = sessionStorage.getItem('sso_2fa_pending');
          
          if (sso2FAContext) {
            const context = JSON.parse(sso2FAContext);
            
            // Check if user has 2FA enabled
            const twoFASettings = await twoFactorAuthService?.get2FASettings();
            
            if (twoFASettings?.enabled && twoFASettings?.enforced) {
              // Clear SSO context
              sessionStorage.removeItem('sso_2fa_pending');
              
              // Redirect to 2FA verification page
              // For now, we'll send a 2FA code
              const defaultMethod = twoFASettings?.default_method || 'email';
              
              if (defaultMethod === 'sms' && twoFASettings?.sms_enabled) {
                await twoFactorAuthService?.sendVerificationCode(
                  'sms',
                  twoFASettings?.sms_phone_number,
                  null,
                  Math.floor(100000 + Math.random() * 900000)?.toString()
                );
              } else if (defaultMethod === 'email' && twoFASettings?.email_enabled) {
                await twoFactorAuthService?.sendVerificationCode(
                  'email',
                  null,
                  session?.user?.email,
                  Math.floor(100000 + Math.random() * 900000)?.toString()
                );
              }
              
              // Store pending 2FA state
              sessionStorage.setItem('pending_2fa_verification', JSON.stringify({
                userId: session?.user?.id,
                method: defaultMethod,
                ssoContext: context
              }));
              
              // Note: In a complete implementation, redirect to a 2FA verification page
              // For now, the user will need to verify via the security settings
            }
          }
          
          setUser(session?.user);
          
          // Log successful login
          await auditLogService?.createLog(
            session?.user?.id,
            auditLogService?.EVENT_TYPES?.LOGIN,
            'User logged in successfully',
            { success: true, metadata: { loginMethod: sso2FAContext ? 'sso' : 'standard' } }
          );
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => {
      subscription?.unsubscribe()
      sessionOperations?.cleanup()
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      sessionOperations?.cleanup()
    }
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      
      // Log authentication attempt
      if (data?.user) {
        await auditLogService?.createLog(
          data?.user?.id,
          auditLogService?.EVENT_TYPES?.LOGIN,
          'User logged in successfully',
          { success: true }
        );
      } else if (error) {
        // For failed login, we don't have user_id, so we'll handle this in the login component
      }
      
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      // Log logout before signing out
      if (user?.id) {
        await auditLogService?.createLog(
          user?.id,
          auditLogService?.EVENT_TYPES?.LOGOUT,
          'User logged out',
          { success: true, metadata: { method: 'manual' } }
        );
      }
      
      // Clean up session tracking
      sessionOperations?.cleanup()
      
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      })

      if (error) {
        // Track failed authentication attempt
        try {
          const deviceInfo = navigator?.userAgent || 'Unknown';
          const ipAddress = null; // Would need external service for IP detection
          const location = null; // Would need geolocation service
          
          // Get user ID if exists (for tracking failed attempts by user)
          const { data: userProfile } = await supabase?.from('user_profiles')?.select('id')?.eq('email', email)?.single();
          
          await patternDetectionService?.trackFailedAuth(
            userProfile?.id || null,
            ipAddress,
            deviceInfo,
            location,
            error?.message || 'Invalid credentials'
          );
        } catch (trackError) {
          console.error('Error tracking failed auth:', trackError);
        }
        
        throw error;
      }

      return { data, error: null }
    } catch (error) {
      console.error('Login error:', error)
      return { data: null, error }
    }
  }

  const signUp = async (email, password, options = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({ email, password, ...options })
      
      // Log sign up attempt
      if (data?.user) {
        await auditLogService?.createLog(
          data?.user?.id,
          auditLogService?.EVENT_TYPES?.SIGNUP,
          'User signed up successfully',
          { success: true }
        );
      } else if (error) {
        // For failed sign up, we don't have user_id, so we'll handle this in the sign up component
      }
      
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    sessionToken,
    securityPreferences,
    setSecurityPreferences,
    currentOrganizationId,
    setCurrentOrganizationId,
    isSessionVerified,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
